import { useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import type { Contact } from "../types";

export type OptInState = "approved" | "denied" | "unknown";

/**
 * Resolve the marketing opt-in state to show on the contact page.
 *
 * Prefers the explicit `whatsapp_consent_state` captured by the bot's opt-in
 * prompt; falls back to deriving it from the gate's `whatsapp_opt_in_status`
 * for contacts that predate consent capture.
 */
export function resolveOptInState(
  contact: Pick<Contact, "whatsapp_consent_state" | "whatsapp_opt_in_status">,
): OptInState {
  const consent = (contact.whatsapp_consent_state ?? "").toLowerCase();
  if (consent === "approved" || consent === "denied" || consent === "unknown") {
    return consent;
  }
  const status = (contact.whatsapp_opt_in_status ?? "").toLowerCase();
  if (status === "opted_in") return "approved";
  if (status === "opted_out") return "denied";
  return "unknown";
}

export function isStopped(contact: Pick<Contact, "whatsapp_stopped">): boolean {
  return contact.whatsapp_stopped === true;
}

const BADGE_VARIANT: Record<OptInState, "default" | "secondary" | "outline"> = {
  approved: "default",
  denied: "secondary",
  unknown: "outline",
};

/** Read-only marketing opt-in status + a "stopped" (do-not-contact) badge. */
export const ContactOptInStatus = ({ contact }: { contact: Contact }) => {
  const translate = useTranslate();
  const state = resolveOptInState(contact);
  const stopped = isStopped(contact);

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">
        {translate("crm.whatsapp.opt_in_label")}
      </span>
      <Badge variant={BADGE_VARIANT[state]}>
        {translate(`crm.whatsapp.opt_in.${state}`)}
      </Badge>
      {stopped && (
        <Badge variant="destructive">{translate("crm.whatsapp.stopped")}</Badge>
      )}
    </div>
  );
};
